import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, FileDown } from "lucide-react";
import { BOX_GALLERY } from "@/data/products";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Vial } from "./Vial";
import { Lightbox } from "./Lightbox";
import { scrollToId } from "@/hooks/useLenis";
import { useLang } from "@/i18n/LanguageContext";
import { productText } from "@/i18n/translations";
import { reveal } from "@/hooks/reveal";

const ChemFormula = ({ formula }) => (
  <>
    {formula.split(/(\d+)/).map((part, i) =>
      /^\d+$/.test(part) ? (
        <sub key={i} className="formula-gold text-[0.72em]">{part}</sub>
      ) : (
        part
      )
    )}
  </>
);

export const VialCard = ({ product, onInquire, index }) => {
  const { t, lang } = useLang();
  const pt = productText(lang, product.id);
  const [mg, setMg] = useState(product.mg[0]);
  const gallery = product.image
    ? [product.image, ...BOX_GALLERY, ...(product.gallery || [])]
    : [];
  const [imgIdx, setImgIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const cycleRef = useRef(null);
  const cardRef = useRef(null);

  const handleTilt = (e) => {
    const el = cardRef.current;
    if (!el || window.innerWidth < 768) return;
    const r = el.getBoundingClientRect();
    const rx = ((e.clientY - r.top) / r.height - 0.5) * -5;
    const ry = ((e.clientX - r.left) / r.width - 0.5) * 7;
    el.style.transform = `perspective(1100px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
  };
  const resetTilt = () => {
    const el = cardRef.current;
    if (el) el.style.transform = "";
  };

  const openLightbox = () => {
    clearInterval(cycleRef.current);
    setLightbox(true);
  };

  const startGallery = () => {
    if (gallery.length < 2) return;
    cycleRef.current = setInterval(
      () => setImgIdx((i) => (i + 1) % gallery.length),
      1400
    );
  };
  const stopGallery = () => {
    clearInterval(cycleRef.current);
    setImgIdx(0);
  };

  const inquire = () => {
    onInquire?.(`${product.ref} · ${mg}`);
    scrollToId("contact");
  };

  return (
    <motion.article
      ref={cardRef}
      {...reveal((index % 3) * 0.1)}
      onMouseMove={handleTilt}
      onMouseLeave={resetTilt}
      style={{ transition: "transform 0.16s ease-out" }}
      className="vial-card group relative flex flex-col glass-gold gold-frame will-change-transform p-6 md:p-7"
      data-testid={`product-card-${product.id}`}
    >
      <div className="card-shine-overlay" />
      {/* header row */}
      {product.ukExclusive && (
        <span
          className="absolute -top-3 right-5 z-20 bg-[var(--al-gold)] text-black font-mono-tech text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 shadow-[0_0_18px_rgba(212,175,55,0.35)]"
          data-testid={`uk-exclusive-badge-${product.id}`}
        >
          UK Exclusive
        </span>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <span className="font-mono-tech text-[10px] tracking-[0.2em] text-[var(--al-gold)]/70 uppercase">
            {product.ref}
          </span>
          <h4 className="font-display font-bold text-white text-lg md:text-xl tracking-tight mt-1 leading-snug">
            {pt.name}
          </h4>
        </div>
        {!product.image && (
          <div className="shrink-0 -mt-2 -mr-1 scale-[0.82] origin-top-right transition-transform duration-500 group-hover:scale-[0.9]">
            <Vial formula={product.formula} />
          </div>
        )}
      </div>

      {/* product render (official high-res image) */}
      {product.image && (
        <div
          className="mt-4 relative w-full aspect-[4/3] overflow-hidden border border-white/[0.08] bg-black cursor-zoom-in"
          onMouseEnter={startGallery}
          onMouseLeave={stopGallery}
          onClick={openLightbox}
          data-testid={`product-gallery-${product.id}`}
        >
          <div className="absolute inset-0 spotlight-slate opacity-60 pointer-events-none z-20" />
          {gallery.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${pt.name} — AlloLabs UK premium box`}
              loading="lazy"
              className={`absolute inset-0 z-10 w-full h-full object-contain transition-all duration-700 group-hover:scale-[1.04] ${
                i === imgIdx ? "opacity-100" : "opacity-0"
              }`}
              data-testid={i === 0 ? `product-image-${product.id}` : undefined}
            />
          ))}
          {gallery.length > 1 && (
            <div className="absolute bottom-2.5 left-0 right-0 z-30 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              {gallery.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 transition-all duration-500 ${
                    i === imgIdx
                      ? "w-5 bg-[var(--al-gold)]"
                      : "w-1.5 bg-white/30"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* formula */}
      <div className="mt-4 border border-white/[0.06] bg-black/40 px-3 py-2">
        <span className="formula-gold font-mono-tech text-[12px] break-all" data-testid={`product-formula-${product.id}`}>
          <ChemFormula formula={product.formula} />
        </span>
      </div>

      {/* description (scrollable) */}
      <div
        className="desc-scroll mt-4 flex-1 min-h-44 overflow-y-auto pr-2"
        data-testid={`product-desc-scroll-${product.id}`}
      >
        <p className="font-body text-[13px] leading-relaxed text-white/55 whitespace-pre-line">
          {pt.use}
        </p>
      </div>

      {/* mg dropdown */}
      <div className="mt-5">
        <label className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/70 mb-2 block">
          {t.vialCard.selectWeight}
        </label>
        <Select value={mg} onValueChange={setMg}>
          <SelectTrigger
            className="w-full bg-[#151515] border-white/10 text-white rounded-none h-11 font-mono-tech text-[13px] focus:ring-1 focus:ring-[var(--al-gold)]/50"
            data-testid={`product-mg-trigger-${product.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
            {product.mg.map((m) => (
              <SelectItem
                key={m}
                value={m}
                className="font-mono-tech text-[13px] focus:bg-[var(--al-gold)]/15 focus:text-[var(--al-gold)] cursor-pointer"
                data-testid={`product-mg-option-${product.id}-${m}`}
              >
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* CoA download (or invisible spacer to keep rows aligned) */}
      {product.coaPdf ? (
        <a
          href={product.coaPdf}
          download
          className="mt-3 w-full border border-white/15 py-3 font-body text-[11px] tracking-[0.2em] uppercase text-white/70 hover:text-[var(--al-gold)] hover:border-[var(--al-gold)]/50 transition-colors duration-300 flex items-center justify-center gap-2"
          data-testid={`product-coa-download-${product.id}`}
        >
          <FileDown className="w-3.5 h-3.5" />
          {t.vialCard.downloadCoa}
        </a>
      ) : (
        <div
          className="mt-3 w-full border border-transparent py-3 font-body text-[11px] tracking-[0.2em] uppercase invisible flex items-center justify-center gap-2"
          aria-hidden="true"
        >
          <FileDown className="w-3.5 h-3.5" />
          {t.vialCard.downloadCoa}
        </div>
      )}

      {product.image && (
        <Lightbox
          open={lightbox}
          images={gallery}
          index={imgIdx}
          setIndex={setImgIdx}
          onClose={() => { setLightbox(false); setImgIdx(0); }}
          alt={`${pt.name} — AlloLabs UK premium box`}
        />
      )}

      {/* inquire */}
      <button
        onClick={inquire}
        className="btn-gold mt-4 w-full border border-[var(--al-gold)]/50 py-3.5 font-body text-[11px] tracking-[0.2em] uppercase text-[var(--al-gold)]"
        data-testid={`product-inquire-${product.id}`}
      >
        <span className="flex items-center justify-center gap-2">
          {t.vialCard.inquire}
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </button>
    </motion.article>
  );
};
